import mori from 'mori';
import { sheetParseConfig as c } from  '../config/config';
import isDatatypeRow from './transducerUtils/isDatatypeRow';
import {
  getHeaderDataTypes,
  getHeadersOnly,
  getHeaderTypeObject } from './transducerUtils/headerUtils';
import { getInferredType, getAndCleanCSVValues } from '../util/util.js';

export function buildHeaders(data, config) {
  function immutableHeadersSingleSheet(headerData) {
    const headersRaw = headerData;
    const headerDataTypes = getHeaderDataTypes(c.dataTypes);
    let rowTypes;
    // repeats in the first row means nested
    const nestedRow = mori.count(mori.first(headersRaw)) >=
      mori.count(mori.distinct(mori.first(headersRaw)));
    const datatypeRow = mori.some((row) => {
      return isDatatypeRow(row, headerDataTypes);
    }, headersRaw);

    // to find out what type of header setup we're dealing with
    // and fill out the appropriate properties
    if (nestedRow && !datatypeRow && headersRaw.length === 2) {
      rowTypes = ['headerGroup', 'characteristic'];
    } else if (datatypeRow && headersRaw.length === 2) {
      rowTypes = ['characteristic', 'type'];
    } else if (datatypeRow && headersRaw.length === 3) {
      rowTypes = ['headerGroup', 'characteristic', 'type'];
    } else {
      rowTypes = ['characteristic'];
    }

    // Make sure to map defaults to types
    let typeRow = rowTypes.indexOf('type');
    const charRow = rowTypes.indexOf('characteristic');
    const groupRow = rowTypes.indexOf('headerGroup');
    if (typeRow !== -1) {
      rowTypes.push('label');
      rowTypes.push('width');
      const labels = headersRaw[typeRow].map((item) =>
        (item || 'Default').split('--')[0]
      );
      const types = headersRaw[typeRow].map((item) => {
        const type = getHeaderTypeObject(item, headerDataTypes);
        return type.type ? type : item;
      });
      // Because of the lookup these need to get flipped
      headersRaw[typeRow] = types.map((x) => (x || {}).type || x);
      headersRaw.push(labels);
      headersRaw.push(types.map((x) => (x || {}).width));
    } else {
      typeRow = rowTypes.length;
      rowTypes.push('type', 'label');
      const types = headersRaw[charRow].map((item) => {
        const inferredType = getInferredType(item);
        return inferredType || 'default';
      });

      const labels = types;
      headersRaw.push(types, labels);
    }

    // For nested rows, we want to build a compound header name
    // to get distinct headers.  Otherwise, nested tables will mistakenly
    // have data views listed when there is only 1 data view
    const composedNestedHeaders =
      groupRow !== -1 ?
        headersRaw[groupRow]
          .reduce((acc, curr, idx) => {
            acc.push(`${curr + headersRaw[charRow][idx]}`);
            return acc;
          }, []) : null;

    // To check for the presence of data views, we compare the distinct
    // headers to all the headers.  If we have nested headers,
    // use the built composed headers above to get distinct header count
    const hasDataviews =
      mori.count(
        mori.distinct(
          nestedRow && composedNestedHeaders ?
            composedNestedHeaders :
            headersRaw[charRow]
        )
      ) < mori.count(headersRaw[charRow]);

    // Add 'idx' to the generated props
    rowTypes.push('idx');

    // There are cases where the last header(s) do not have header groups.
    // With these cases, we need to pad the group row with nulls.
    // This will accurately build the headers based on how many characteristics
    // there are
    if (nestedRow && headersRaw[groupRow]) {
      headersRaw[groupRow] = headersRaw[charRow].reduce(
        (acc, curr, idx) =>
          acc.concat(headersRaw[groupRow][idx] || null), []
        );
    }

    let immutableHeaders = mori.into(mori.vector(), mori.map(
      (col) => mori.zipmap(rowTypes, col),
      mori.map(
        mori.vector,
        ...headersRaw,
        mori.range(mori.count(mori.first(headersRaw))))));

    // Set default base column if not explicitly defined
    if (!mori.get('characteristic', mori.first(immutableHeaders))) {
      let baseType = 'geography';
      const context = mori.getIn(config, ['globalRules', 'Context'], false);
      if (!context) {
        immutableHeaders = mori.assoc(immutableHeaders, 0, mori.hashMap(
          c.headerTypes[0], 'Location',
          c.headerTypes[1], baseType,
          'label', (baseType && baseType[0].toUpperCase() + baseType.slice(1)),
          'idx', 0
        ));
      } else {
        if (!(/^state|country$/i.test(mori.first(context)))) {
          baseType = 'text';
        }
        immutableHeaders = mori.assoc(immutableHeaders, 0, mori.hashMap(
          c.headerTypes[0], mori.first(context),
          c.headerTypes[1], baseType,
          'label', (baseType && baseType[0].toUpperCase() + baseType.slice(1)),
          'idx', 0
        ));
      }
    }

    // For settings targeted at specific columns assign them where needed
    if (mori.count(mori.get(config, 'settings')) ||
        mori.count(mori.get(config, 'globalSettings'))) {
      immutableHeaders = mori.map((header) => {
        const characteristic = mori.get(header, 'characteristic');
        const headerGroup = mori.get(header, 'headerGroup', false);
        const headerType = mori.get(header, 'label', 'default');
        const lowerHeaderType = headerType.toLowerCase();

        // Progressivly determine the specificity of the selector
        // If the column already has a type, it can't be overridden by a
        // global rule
        const headerTypeSelector = lowerHeaderType !== 'default' ?
          headerType :
          characteristic;

        // Selector that best targets column rules
        // For nested tables, we want the group header and the characteristic
        // becomes the "data format"
        const fullSelector = mori.conj(
          (headerGroup ?
            mori.hashMap('Distribution', headerGroup) :
            mori.hashMap('Distribution', characteristic)),
          ((headerTypeSelector !== 'default' && headerGroup) ?
            mori.hashMap(
              'Data Format', headerTypeSelector.toLowerCase()
            ) :
            mori.hashMap())
        );

        // Data Format settings are special because they can be either selectors
        // or settings. For example:
        //
        // any,Characteristic Name,Percent,any,Type,Setting
        //
        // can set the Data Format equal to "Percent" and add a Type:Setting pair
        // to a column, or it can add the Type:Setting pair to a column by
        // selecting on an already defined Data Format ("Percent")
        let dataFormat = mori.getIn(
          config,
          mori.vector(
            'settings',
            'settings',
            mori.hashMap(
              'Data Format',
              lowerHeaderType
            )
          ),
          // if headerType doesn't have a match, also check what counts for the
          // characteristic for this column
          mori.getIn(
            config,
            mori.vector(
              'settings',
              'settings',
              mori.assoc(
                fullSelector,
                'Data Format',
                (
                  headerGroup ? characteristic : headerTypeSelector
                ).toLowerCase()
              )
            ),
            mori.getIn(
              config,
              mori.vector(
                'settings',
                'settings',
                mori.hashMap(
                  'Data Format',
                  (headerGroup ? characteristic : headerTypeSelector)
                    .toLowerCase()
                )
              ),
              // try && syntax
              mori.getIn(
                config,
                mori.vector(
                  'settings',
                  'settings',
                  mori.hashMap(
                    'Distribution', `${headerGroup} && ${characteristic}`,
                    'Data Format', headerTypeSelector.toLowerCase()
                  )
                ),
                // try global settings as well
                mori.getIn(
                  config,
                  mori.vector(
                    'settings',
                    'settings',
                    mori.hashMap(
                      'Data Format',
                      lowerHeaderType
                    )
                  ),
                  mori.hashMap()
                )
              )
            )
          )
        );

        // Using the best selector, get matching rules
        const filteredSettings = mori.filter(
          // don't use loose selector for now
          /*
          (setting) => (
            mori.equals(mori.first(setting), fullSelector) ||
            mori.equals(mori.dissoc(mori.first(setting), 'Data Format'),
              fullSelector)
          ),
          */
          (setting) => mori.equals(mori.first(setting), fullSelector),
          mori.getIn(config, ['settings', 'settings'])
        );

        // Finds rules that apply to only the distribution
        const selector = mori.getIn(
          config,
          ['settings', 'settings', mori.dissoc(fullSelector, 'Data Format')],
          mori.hashMap()
        );

        // a hack to ensure the dataview label retains it's original formatting
        if (mori.get(dataFormat, 'label') !== headerType) {
          dataFormat = mori.assoc(
            dataFormat,
            'label',
            headerType
          );
        }
        // Apply found rules
        return mori.reduce(
          // extend column def with found rules
          (acc, value) => mori.conj(
            acc,
            mori.first(mori.rest(value))
          ),
          // extend column def with global settings, then data format
          mori.into(
            header,
            mori.vector(
              mori.getIn(
                config,
                ['settings', 'globalSettings'],
                mori.hashMap()
              ),
              selector,
              dataFormat
            )
          ),
          filteredSettings
        );
      }, immutableHeaders);
    }
    return { immutableHeaders, hasDataviews, js: mori.toJs(immutableHeaders) };
  }

  return {
    ...immutableHeadersSingleSheet(getHeadersOnly(getOneSheet(data))),
    immutableHeadersAll: data
      // Get Raw Headers
      .map((dataItem) => [
        dataItem[0],
        getHeadersOnly(getOneSheet([dataItem])),
      ])
      // Filter out empty header sets (Notes tab)
      .filter((dataItem) => dataItem[1].length)
      // Reduce to a map of {Shee_name: {headers}}
      .reduce((acc, curr) => ({
        [curr[0]]: {
          // We call getHeadersOnly and getOneSheet above as well
          // We need to do it again to change as little code as possible
          // for multi-sheet column definitions KFF-2819
          ...immutableHeadersSingleSheet(getHeadersOnly(getOneSheet([curr]))),
        },
        ...acc,
      }), {}),
  };
}

/**
 * A transducer that grabs the first data sheet from the google doc.
 * @param {object} data - The gdocs json data
 * @return {array} - The "sheet" array from the data sheet
 */
export function getOneSheet(data, config = false) {
  return mori.first(mori.intoArray(mori.transduce(
    mori.comp(
      mori.filter((x) => {
        return config ?
          c.configTitles.indexOf(x[0].toLowerCase()) > -1 :
          c.configTitles.indexOf(x[0].toLowerCase()) === -1;
      }),
      mori.map((x) => x[1])
    ),
    mori.completing(mori.conj),
    mori.vector(),
    data
  )));
}

/**
 * Returns the config array
 * @param {object} data - The gdocs json data
 * @return {object} The config object
 */
export function getConfig(data) {
  const configSheet = getOneSheet(data, true);

  return mori.reduce(
    (acc, next) => mori.conj(
      acc,
      mori.hashMap(
        mori.first(next),
        mori.reduce(
          // Each type of config object get processed differently
          getRuleReducer(mori.first(next)),
          mori.hashMap(),
          mori.first(mori.rest(next))
        )
      )
    ),
    mori.hashMap(),
    mori.groupBy(
      configGroupBy,
      cleanAndBuildConfigSheet(configSheet)
    )
  );
}

/**
 * Return the appropriate reducing function
 *
 * @param {string} type The name of the bucket we're currently building
 *
 * @return {function} A reducer function
 */
function getRuleReducer(type) {
  switch (type) {
    case 'globalRules':
      return buildGlobalRules;
    case 'notesSources':
      return buildNotesSources;
    case 'footnotes':
      return buildFootnotes;
    case 'settings':
      return buildSettings;
    default:
      return mori.conj;
  }
}

/**
 * Builds settings objects from a list of config objects
 *
 * @param {hashMap} acc Current accumulator value map
 * @param {hashMap} next The current row object
 *
 * @return {hashMap} A map of settings objects for lookup and rendering
 */
function buildSettings(acc, next) {
  let settings = mori.get(acc, 'settings', mori.hashMap());
  let globalSettings = mori.get(acc, 'globalSettings', mori.hashMap());

  /**
   * settings stored by complex key key
   *
   * ("Timeframe", "Distribution", "Data Format", "Geography"): Type:Note[Setting:Value]
   */
  const paths = mori.reduce(
    (pathAcc, key) => {
      const addValues = getAndCleanCSVValues(mori.get(next, key), ['any']);

      return mori.mapcat(
        (val) => {
          const dataVal = key === 'Data Format' ? val.toLowerCase() : val;
          const newTuple = dataVal !== 'any' ?
            mori.hashMap(key, dataVal) :
            mori.hashMap();
          if (!mori.count(pathAcc)) {
            return mori.vector(newTuple);
          }
          return mori.map(
            (parentVal) => {
              return mori.conj(parentVal, newTuple);
            },
            pathAcc
          );
        },
        addValues
      );
    },
    mori.vector(),
    mori.vector('Timeframe', 'Distribution', 'Data Format', 'Geography')
  );

  const currSetting = mori.hashMap(
    mori.get(next, 'Type'),
    mori.get(next, 'Note')
  );

  // unspecific rules classified as global
  if (mori.every((item) => item === 'any', mori.first(paths))) {
    globalSettings = mori.into(
      globalSettings,
      currSetting
    );
  } else {
    const headerDataTypes = getHeaderDataTypes(c.dataTypes);
    // extends settings objects when complex keys match
    settings = mori.reduce(
      (acc2, curr2) => mori.reduce(
        (acc3, curr3) => mori.assoc(
          acc3,
          mori.first(curr3),
          mori.merge(
            mori.get(acc2, mori.first(curr3)),
            mori.last(curr3)
          )
        ),
        acc2,
        curr2
      ),
      settings,
      mori.map(
        (vect) => mori.hashMap(
          vect,
          getSettingWithDefaults(vect, currSetting, headerDataTypes)
        ),
        paths
      )
    );
  }

  return mori.hashMap(
    'settings', settings,
    'globalSettings', globalSettings
  );
}

/**
 * Builds data format options by label type
 *
 * @param {hashMap} current The hashMap representing the path this rule will
 *  be assigned to
 * @param {hashMap} setting The base setting to extend
 * @param {object} datatypes The datatypes list from config
 *
 * @return {hashMap} Extended setting for this rule path
 */
function getSettingWithDefaults(current, setting, datatypes) {
  const dataFormat = mori.get(current, 'Data Format', 'any');
  const dataFormatType = getHeaderTypeObject(
    dataFormat,
    datatypes
  );

  const dataFormatSetting = (dataFormat && dataFormat !== 'any') ?
    mori.hashMap(
      'label', dataFormat || 'Default',
      'type', dataFormatType ? dataFormatType.type : 'default'
    ) :
    mori.hashMap();

  return mori.conj(
    setting,
    dataFormatSetting
  );
}

/**
 * Builds footnote reference objects from a list of config objects
 *
 * @param {hashMap} acc Current accumulator value map
 * @param {hashMap} next The current row object
 *
 * @return {hashMap} A map of footnote reference structures for lookup
 * and rendering
 */
function buildFootnotes(acc, next) {
  let footnotes = mori.get(acc, 'footnotes', mori.hashMap());
  let footnoteList = mori.get(acc, 'footnoteList', mori.hashMap());
  let foundFootnotes = mori.get(acc, 'foundFootnotes', mori.hashMap());
  const note = mori.get(next, 'Note', false);

  // Footnotes go in a nested hashMap for us to look up
  // find by the set of selectors
  const isUsed = mori.get(foundFootnotes, mori.hash(note.toLowerCase()), false);
  const footnoteUid = isUsed || mori.count(foundFootnotes) + 1;
  if (isUsed === false) {
    foundFootnotes = mori.assoc(
      foundFootnotes,
      mori.hash(note.toLowerCase()),
      footnoteUid
    );
  }
  const timeframes = getAndCleanCSVValues(
    mori.get(next, 'Timeframe'), ['any']
  );
  const geographies = getAndCleanCSVValues(
    mori.get(next, 'Geography'), ['any']
  );
  const distributions = getAndCleanCSVValues(
    mori.get(next, 'Distribution'), ['any']
  );

  timeframes.forEach((timeframe) => {
    geographies.forEach((geo) => {
      distributions.forEach((dist) => {
        // Normalize base column header for footnote targets
        const mapDist = dist === 'Region' ? 'Location' : dist;
        const locationSplit = geo.match(/^([^[]+)\[(.*)\]$/);
        if (mapDist.indexOf(' && ') > -1) {
          const splitDist = mapDist.split(' && ');
          footnotes = mori.updateIn(
            footnotes,
            [timeframe, geo, mori.hashMap(...splitDist)],
            // add multiple footnotes in place
            (value) => mori.conj(
              (value || mori.vector()),
              mori.hashMap(
                'note', note,
                'id', footnoteUid,
                'value', locationSplit ? locationSplit[2] : false,
              )
            )
          );
        } else {
          footnotes = mori.updateIn(
            footnotes,
            [timeframe, geo, mapDist],
            // add multiple footnotes in place
            (value) => mori.conj(
              (value || mori.vector()),
              mori.hashMap(
                'note', note,
                'id', footnoteUid,
                'value', locationSplit ? locationSplit[2] : false,
              )
            )
          );
        }

        footnoteList = mori.assoc(
          footnoteList,
          footnoteUid,
          note
        );
      });
    });
  });

  return mori.hashMap(
    'footnotes', footnotes,
    'footnoteList', footnoteList,
    'foundFootnotes', foundFootnotes
  );
}

/**
 * Builds notes and sources arrays from a list of config objects
 *
 * @param {hashMap} acc Current accumulator value map
 * @param {hashMap} next The current row object
 *
 * @return {hashMap} A map of vectors for each type of Notes/Sources
 */
function buildNotesSources(acc, next) {
  const type = mori.get(next, 'Type');
  return mori.conj(
    acc,
    mori.hashMap(
      type,
      mori.into(
        mori.get(acc, type, mori.vector()),
        mori.vector(next)
      )
    )
  );
}

/**
 * Builds global rules hash map from a list of config objects
 *
 * @param {hashMap} acc Current accumulator value map
 * @param {hashMap} next The current row object
 *
 * @return {hashMap} A map of rules in `Type: Note` form
 */
function buildGlobalRules(acc, next) {
  const type = mori.get(next, 'Type');
  const typeLower = type.toLowerCase();
  const note = mori.get(next, 'Note');
  const multiGeoTypes = [
    'map base column',
    'link column',
    'link target',
  ];
  let rule;

  // Context rules can be joined
  if (typeLower === 'context') {
    rule = note.split('+');
  } else if (typeLower === 'related columns') {
    rule = getAndCleanCSVValues(note);
  } else if (multiGeoTypes.indexOf(typeLower) > -1) {
    rule = note;
  } else {
    rule = note.toLowerCase() === 'yes';
  }

  return mori.conj(
    acc,
    mori.hashMap(type, rule)
  );
}

/**
 * Cleans the config sheet to remove presentational rows and builds objects
 * from headers for parsing
 *
 * @param {array} sheet The config sheet removed from the original data
 *
 * @return {vector} A list of rows that have been mapped to headers
 */
function cleanAndBuildConfigSheet(sheet) {
  // normalize Note/Notes in header keys
  const headers = mori.map(
    (key) => ['Notes', 'Note'].indexOf(key) > -1 ? 'Note' : key,
    mori.first(sheet)
  );
  return mori.map(
    // Create an object from the row so we can lookup by name
    (row) => mori.zipmap(headers, row),
    // Discard blank dividing rows and row without a Note value
    mori.remove(
      (row) => mori.every((x) => x === null, mori.rest(row)) ||
        mori.last(row) === null,
      mori.rest(sheet)
    )
  );
}

/**
 * A groupBy function to split config sheets into proper buckets.
 *
 * @param {hashMap} row The current row being evaluated
 *
 * @return {string} The bucket name for this row
 */
function configGroupBy(row) {
  if (configIsGlobalRule(row)) {
    return 'globalRules';
  } else if (configIsNotesSources(row)) {
    return 'notesSources';
  } else if (configIsFootnote(row)) {
    return 'footnotes';
  }
  return 'settings';
}

/**
 * Function to test whether current row is a global rule
 *
 * @param {hashMap} row The current row being evaluated
 *
 * @return {bool} isGlobalRule?
 */
function configIsGlobalRule(row) {
  const distribution = mori.get(row, 'Distribution', 'any');
  return mori.hasKey(row, 'Type') &&
         mori.hasKey(row, 'Note') &&
         (!distribution || distribution === 'any') &&
         !mori.get(row, 'Data Format', false) &&
         c.globalRules.indexOf(mori.get(row, 'Type', '').toLowerCase()) > -1;
}

/**
 * Function to test whether current row is Notes/Sources
 *
 * @param {hashMap} row The current row being evaluated
 *
 * @return {bool} isNotesSources?
 */
function configIsNotesSources(row) {
  return mori.hasKey(row, 'Type') &&
         mori.hasKey(row, 'Note') &&
         c.sourceTypes.indexOf(
          (mori.get(row, 'Type', '') || '').toLowerCase()) > -1;
}

/**
 * Function to test whether current row is a footnote
 *
 * @param {hashMap} row The current row being evaluated
 *
 * @return {bool} isFootnote?
 */
function configIsFootnote(row) {
  return (mori.get(row, 'Type', '') || '').toLowerCase() === 'footnote';
}



/** WEBPACK FOOTER **
 ** ../~/eslint-loader!./js/services/transducers.js
 **/